import { RefObject, useEffect, useMemo, useState } from 'react';

import { FingerprintHelper } from '@bcp-channels-sharedcomponents-reactjs/utils';
import { Box } from '@bcp-nextgen-dx-component-factory/design-system';
import { ApiConfig } from '@configs';
import { useBrowserInfoCookie, useCookiesConsent, useLoginForm } from '@hooks';
import { useSearchParamsStore, useServicesStore } from '@store';
import { ChallengeTypeEnum, CredentialsChallenge, SafeCredentialTypeEnum, endpoints } from '@types';

export const FormHidden = ({
	submitBtnRef,
	withCertificate,
	safeCredentialType,
}: {
	submitBtnRef?: RefObject<HTMLButtonElement | null>;
	withCertificate?: boolean;
	safeCredentialType?: SafeCredentialTypeEnum;
}) => {
	const { userAgent } = window.navigator;
	const {
		userAlias,
		userAliasTypeId,
		challengeType,
		returnUrl,
		sdcSessionId,
		sdcBusinessSessionId,
		positionsToAsk,
		needsTaxNumber,
		needsSafe,
		geo,
		targetSystem,
		targetInfrastructure,
	} = useServicesStore((store) => store.accountLogin) || {};
	const captchaId = useServicesStore((store) => store.accountGenerateCaptcha?.id);
	const language = useSearchParamsStore((state) => state.language);

	const { multichannel, password, taxNumber, sms, token, captchaValue } = useLoginForm();
	const { getFormattedCookiesConsent } = useCookiesConsent();
	const { getBrowserInfoCookie } = useBrowserInfoCookie();
	const [fingerprint, setFingerprint] = useState('');

	const cookieConsent = getFormattedCookiesConsent();
	const cookieCifra = getBrowserInfoCookie();

	const actionEndpoint = useMemo(() => {
		const endpoint = endpoints.accountChallenge;
		const certificateEndpoint = ApiConfig.certificateUrl + '/account/challenge/fe';
		return withCertificate ? certificateEndpoint : endpoint;
	}, [withCertificate]);

	const credentials = useMemo(() => {
		let credentialsChallenge: CredentialsChallenge[] | undefined = undefined;
		let additionalChallengeType: ChallengeTypeEnum | undefined = undefined;
		let safeCredentialValue: string | undefined = undefined;

		if (
			challengeType === ChallengeTypeEnum.Multichannel ||
			challengeType === ChallengeTypeEnum.MultichannelCompanyOperatorCoHolder
		) {
			credentialsChallenge = positionsToAsk?.map((position, index) => ({
				Position: position,
				Value: multichannel?.[index],
			}));
		}

		if (needsTaxNumber) {
			additionalChallengeType = ChallengeTypeEnum.Nif;
			credentialsChallenge = positionsToAsk?.map((position, index) => ({
				Position: position,
				Value: taxNumber?.[index],
			}));
		}

		if (needsSafe && safeCredentialType === SafeCredentialTypeEnum.Sms) {
			safeCredentialValue = sms;
		}

		if (needsSafe && safeCredentialType === SafeCredentialTypeEnum.Token) {
			safeCredentialValue = (token?.input1 || '') + (token?.input2 || '');
		}

		return { credentialsChallenge, additionalChallengeType, safeCredentialValue };
	}, [
		challengeType,
		multichannel,
		needsSafe,
		needsTaxNumber,
		positionsToAsk,
		safeCredentialType,
		sms,
		taxNumber,
		token?.input1,
		token?.input2,
	]);

	/* get fingerprint asynchronously */
	useEffect(() => {
		const getFingerprint = async () => {
			const result = await FingerprintHelper.getFingerprint();
			setFingerprint(result);
		};

		getFingerprint();
	}, []);

	/* if the actionEndpoint changes, and includes cert, submit the form */
	useEffect(() => {
		if (ApiConfig.certificateUrl && actionEndpoint.includes(ApiConfig.certificateUrl)) {
			submitBtnRef?.current?.click();
		}
	}, [actionEndpoint, submitBtnRef]);

	/* if the credentials.safeCredentialValue changes, submit the form */
	useEffect(() => {
		if (credentials.safeCredentialValue) {
			submitBtnRef?.current?.click();
		}
	}, [credentials.safeCredentialValue, submitBtnRef]);

	/* render input field if value is defined */
	const renderInputField = (name: string, value?: string | null) => {
		return !!value && <input name={name} value={value} readOnly />;
	};

	return (
		<Box styles={{ flexDirection: 'column', height: 0, visibility: 'hidden', position: 'absolute', zIndex: -2 }}>
			<form action={actionEndpoint} method="post" autoComplete="off">
				{renderInputField('fingerprint', fingerprint)}
				{renderInputField('userAgent', userAgent)}
				{renderInputField('sdcBusinessSessionId', sdcBusinessSessionId)}
				{renderInputField('geo', geo)}
				{renderInputField('targetSystem', targetSystem)}
				{renderInputField('targetInfrastructure', targetInfrastructure)}
				{renderInputField('cookieConsent', cookieConsent)}
				{renderInputField('cookieCifra', cookieCifra)}
				{renderInputField('sdcSessionId', sdcSessionId)}
				{renderInputField('returnUrl', returnUrl)}
				{renderInputField('userAlias', userAlias)}
				{renderInputField('userAliasTypeId', userAliasTypeId?.toString())}
				{renderInputField('challengeType', challengeType?.toString())}
				{renderInputField('safeCredentialType', safeCredentialType?.toString())}
				{renderInputField('password', password)}
				{renderInputField('additionalChallengeType', credentials.additionalChallengeType?.toString())}
				{renderInputField('safeCredentialValue', credentials.safeCredentialValue)}
				{renderInputField('captchaId', captchaId)}
				{renderInputField('valueCaptchaClient', captchaValue)}
				{renderInputField('language', language?.toUpperCase() || 'PT')}

				{!!credentials.credentialsChallenge && (
					<input
						name="credentialsChallenge"
						value={JSON.stringify(credentials.credentialsChallenge)}
						readOnly
					/>
				)}

				<button type="submit" ref={submitBtnRef} />
			</form>
		</Box>
	);
};
